import React, { useEffect, useState, useCallback, useRef } from "react";
import { LayoutGrid, FileStack, MessageSquareText, Camera, Trash2, RefreshCw, FileText, CalendarClock, AlertTriangle, ShieldCheck, Send, CheckCircle2, Loader2, LifeBuoy, Database, ClipboardList, CalendarDays, History, BarChart3, Settings as SettingsIcon, ArrowRight, Building2, Download, FileSignature, CreditCard, Truck, Boxes, Radar } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import DashShell from "@/components/dashboard/DashShell";
import AnalysisView from "@/components/dashboard/AnalysisView";
import CoachPanel from "@/components/dashboard/CoachPanel";
import ConfidenceBadge from "@/components/dashboard/ConfidenceBadge";
import BusinessMemory from "@/components/dashboard/BusinessMemory";
import TasksView from "@/components/dashboard/Tasks";
import ComplianceCalendar from "@/components/dashboard/ComplianceCalendar";
import Timeline from "@/components/dashboard/Timeline";
import Insights from "@/components/dashboard/Insights";
import Settings from "@/components/dashboard/Settings";
import Billing from "@/components/dashboard/Billing";
import Submissions, { SubmitForMeButton } from "@/components/dashboard/Submissions";
import ComplianceModules, { ModuleBadge } from "@/components/dashboard/ComplianceModules";
import ComplianceRoadmap from "@/components/dashboard/ComplianceRoadmap";
import CommandCenter, { HealthScoreBadge } from "@/components/dashboard/CommandCenter";
import Portfolio from "@/components/dashboard/Portfolio";
import CompanyAdmin from "@/components/dashboard/CompanyAdmin";
import NotificationBell from "@/components/dashboard/NotificationBell";
import MorningBriefing from "@/components/dashboard/MorningBriefing";
import { DoxMascot, BrandHero } from "@/components/Logo";
import ErrorBoundary from "@/components/ErrorBoundary";
import OnboardingWizard from "@/components/OnboardingWizard";
import DashboardAlerts from "@/components/dashboard/DashboardAlerts";
import StatusRibbon from "@/components/dashboard/StatusRibbon";
import TodaysActivity from "@/components/dashboard/TodaysActivity";
import DashboardFirstRun from "@/components/DashboardFirstRun";
import DoxVerifiedLine from "@/components/DoxVerifiedLine";
import WorkHistorySection from "@/components/WorkHistorySection";
import ZenOverview from "@/components/ZenOverview";
import { useDoxWork } from "@/context/DoxWorkContext";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";
import { requestNotifyPermission, pushBrowserNotification } from "@/lib/notify";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "portfolio", label: "All Businesses", icon: Building2 },
  { key: "documents", label: "Dox's Desk", icon: FileStack },
  { key: "work_history", label: "Dox's Work History", icon: History },
  { key: "memory", label: "Business Memory", icon: Database },
  { key: "tasks", label: "Tasks", icon: ClipboardList },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "timeline", label: "Timeline", icon: History },
  { key: "insights", label: "Insights", icon: BarChart3 },
  { key: "company", label: "Company", icon: Building2 },
  { key: "roadmap", label: "Roadmap", icon: Radar },
  { key: "command_center", label: "Command Center", icon: CalendarClock },
  { key: "modules", label: "Practice Areas", icon: Boxes },
  { key: "submissions", label: "Filings", icon: Truck },
  { key: "ask", label: "Talk to Dox", icon: MessageSquareText },
  { key: "billing", label: "Billing", icon: CreditCard },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Dashboard() {
  const [active, setActive] = useState("overview");
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [notifBump, setNotifBump] = useState(0);
  const [briefing, setBriefing] = useState(null);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [conflicts, setConflicts] = useState(0);
  const [priorities, setPriorities] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { active: activeBiz } = useBusiness();
  const { runDoxWork } = useDoxWork();
  const prevStatus = useRef({});
  const replaceInputRef = useRef(null);
  const replaceTargetId = useRef(null);

  // First-run wizard: fire whenever the active business has no states/trades yet.
  useEffect(() => {
    if (!activeBiz) return;
    const skipped = sessionStorage.getItem(`onb-skip-${activeBiz.id}`);
    const needsOnboarding = !((activeBiz.states || []).length && (activeBiz.trades || []).length);
    if (needsOnboarding && !skipped) setShowOnboarding(true);
  }, [activeBiz]);

  // First-run cinematic: the wizard flips this flag on finish so the very
  // first dashboard render after onboarding plays the "specialists lighting
  // up" motion. Flag consumed once, then cleared.
  const [firstRun, setFirstRun] = useState(() => {
    try { return localStorage.getItem("dox-first-run") === "1"; } catch { return false; }
  });
  useEffect(() => {
    if (!firstRun) return;
    try { localStorage.removeItem("dox-first-run"); } catch { /* no-op */ }
  }, [firstRun]);

  // Post-OAuth toast — GCal callback redirects back with ?gcal=connected|error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcal = params.get("gcal");
    if (gcal === "connected") {
      toast.success("Calendar reconnected — I'm catching up on your renewals now.");
    } else if (gcal === "error") {
      toast.error("Calendar reconnect failed. Try again from Settings.");
    }
    if (gcal) {
      // Strip the query param so refreshes don't re-fire the toast
      params.delete("gcal");
      const next = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState(null, "", next);
    }
  }, []);

  const detectCompletions = useCallback((incoming) => {
    incoming.forEach((d) => {
      const before = prevStatus.current[d.id];
      if (before === "processing" && d.status !== "processing") {
        const a = d.analysis || {};
        const title = a.notification_title || "Dox finished";
        const body = a.notification_body || "";
        if (d.status === "needs_attention") {
          pushBrowserNotification(title, body);
          toast.warning(title, { description: body });
          setNotifBump((n) => n + 1);
        } else if (d.status === "analyzed") {
          pushBrowserNotification(title, body);
          if (d.requires_approval) toast.warning(title, { description: body });
          else toast.success(title, { description: body });
          setNotifBump((n) => n + 1);
        }
      }
      prevStatus.current[d.id] = d.status;
    });
  }, []);

  const loadDocs = useCallback(async () => {
    try {
      const { data } = await api.get("/documents");
      detectCompletions(data);
      setDocs(data);
      return data;
    } catch { return []; }
  }, [detectCompletions]);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/dashboard/stats"); setStats(data); } catch { /* ignore */ }
    try { const { data } = await api.get("/memory"); setConflicts((data.pending_conflicts || []).length); } catch { /* ignore */ }
    try { const { data } = await api.get("/priorities"); setPriorities(data); } catch { /* ignore */ }
  }, []);

  const loadAll = useCallback(async () => { await Promise.all([loadDocs(), loadStats()]); }, [loadDocs, loadStats]);

  useEffect(() => {
    requestNotifyPermission();
    loadAll();
    api.get("/briefing").then(({ data }) => {
      if (data.has_updates) { setBriefing(data); setBriefingOpen(true); }
    }).catch(() => {});
  }, [loadAll]);

  // Live polling while Dox is working on something
  const processing = docs.some((d) => d.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const id = setInterval(async () => {
      const data = await loadDocs();
      if (!data.some((d) => d.status === "processing")) { loadStats(); setNotifBump((n) => n + 1); }
    }, 1600);
    return () => clearInterval(id);
  }, [processing, loadDocs, loadStats]);

  // Compute a live, employee-style status for the "Dox is Working" badge.
  // Priority: processing > waiting approval > preparing renewal > submitted > completed/on the clock
  const doxStatus = React.useMemo(() => {
    const processingCount = docs.filter((d) => d.status === "processing").length;
    if (processingCount) {
      return { key: "reviewing", label: processingCount > 1 ? `reviewing ${processingCount} documents` : "reviewing documents", tone: "emerald", pulse: true };
    }
    const needsApproval = docs.filter((d) => d.status === "analyzed" && d.requires_approval).length;
    if (needsApproval) {
      return { key: "approval", label: needsApproval > 1 ? `waiting for your approval on ${needsApproval} items` : "waiting for your approval", tone: "amber", pulse: true };
    }
    const needsAttention = docs.some((d) => d.status === "needs_attention");
    if (needsAttention) {
      return { key: "attention", label: "needs your attention", tone: "rose", pulse: true };
    }
    const upcoming = priorities?.upcoming_deadlines?.length || 0;
    if (upcoming) {
      return { key: "preparing", label: upcoming > 1 ? `preparing ${upcoming} renewals` : "preparing renewal", tone: "sky", pulse: true };
    }
    const submitted = (priorities?.recent_activity || []).some((a) => (a.type || "").includes("submitted"));
    if (submitted) return { key: "submitted", label: "submitted to state", tone: "violet", pulse: false };
    return { key: "onclock", label: "on the clock", tone: "emerald", pulse: false };
  }, [docs, priorities]);

  const openDocById = async (docId) => {
    const found = docs.find((d) => d.id === docId);
    if (found) { setSelected(found); return; }
    try { const { data } = await api.get(`/documents/${docId}`); setSelected(data); } catch { /* ignore */ }
  };

  const approve = async (docId) => {
    // Dox performs the work — Assign → Depart → Work → Return.
    if (runDoxWork) {
      runDoxWork({
        ackLine: "I've got it.",
        deliverLine: "Everything checked out.",
        stages: [
          { label: "Reviewing your document", emoji: "📄" },
          { label: "Verifying against state records", emoji: "✅" },
          { label: "Checking compliance", emoji: "🛡️" },
          { label: "Filing on your behalf", emoji: "✍️" },
        ],
        run: () => api.post(`/documents/${docId}/approve`),
        onResolved: ({ data }) => {
          setSelected(data);
          toast.success("Dox handled it.");
          loadAll();
          setNotifBump((n) => n + 1);
        },
        onError: () => toast.error("Could not approve."),
      });
      return;
    }
    // Fallback when the provider isn't mounted (defensive).
    try {
      const { data } = await api.post(`/documents/${docId}/approve`);
      setSelected(data);
      toast.success("Approved — Dox is handling it from here.");
      loadAll();
      setNotifBump((n) => n + 1);
    } catch { toast.error("Could not approve."); }
  };

  const requestRetake = (docId) => {
    replaceTargetId.current = docId;
    replaceInputRef.current?.click();
  };

  const onReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const docId = replaceTargetId.current;
    if (!file || !docId) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/documents/${docId}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.message("Thanks — Dox is taking a fresh look.", { description: file.name });
      setSelected(null);
      setActive("documents");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the new copy.");
    }
  };

  const dismissBriefing = async () => {
    setBriefingOpen(false);
    try { await api.post("/briefing/seen"); } catch { /* ignore */ }
  };
  const briefingPriority = (docId) => { dismissBriefing(); setActive("documents"); openDocById(docId); };

  return (
    <DashShell
      nav={NAV}
      active={active}
      onNavigate={setActive}
      title={NAV.find((n) => n.key === active)?.label}
      headerRight={<>
        <HealthScoreBadge />
        <NotificationBell bump={notifBump} onNavigateDoc={(id) => { setActive("documents"); openDocById(id); }} />
      </>}
      doxStatus={doxStatus}
    >
      <DashboardAlerts onGoSettings={() => setActive("settings")} />
      <ErrorBoundary key={active} label={active.replace(/_/g, " ")}>
      {active === "overview" && <ZenOverview onNav={setActive} />}
      {active === "work_history" && <WorkHistorySection variant="page" />}
      {active === "portfolio" && <Portfolio />}
      {active === "documents" && <Documents docs={docs} reload={loadAll} onOpen={setSelected} onRetake={requestRetake} />}
      {active === "company" && <CompanyAdmin />}
      {active === "memory" && <BusinessMemory onChange={() => setNotifBump((n) => n + 1)} />}
      {active === "tasks" && <TasksView onOpenDoc={(id) => { setActive("documents"); openDocById(id); }} />}
      {active === "calendar" && <ComplianceCalendar />}
      {active === "timeline" && <Timeline />}
      {active === "insights" && <Insights />}
      {active === "settings" && <Settings />}
      {active === "billing" && <Billing />}
      {active === "submissions" && <Submissions />}
      {active === "modules" && <ComplianceModules />}
      {active === "roadmap" && <ComplianceRoadmap />}
      {active === "command_center" && <CommandCenter />}
      {active === "ask" && <AskDox hasDocs={docs.length > 0} doxStatus={doxStatus} />}
      </ErrorBoundary>

      {showOnboarding && (
        <OnboardingWizard
          business={activeBiz}
          onClose={() => { sessionStorage.setItem(`onb-skip-${activeBiz?.id}`, "1"); setShowOnboarding(false); }}
          onComplete={() => { setShowOnboarding(false); loadAll(); setActive("command_center"); }}
        />
      )}

      {firstRun && !showOnboarding && (
        <DashboardFirstRun onDone={() => setFirstRun(false)} />
      )}

      <input ref={replaceInputRef} data-testid="replace-input" type="file" className="hidden" onChange={onReplaceFile} accept=".pdf,.png,.jpg,.jpeg,.txt,.csv,.docx" />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl glass-strong border-white/10 text-white max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-white">
              <FileText size={18} className="text-dox-orange" />
              <span className="truncate">{selected?.original_filename}</span>
              {selected?.analysis?.module_id && (
                <ModuleBadge moduleId={selected.analysis.module_id} moduleName={selected.analysis.module_name} />
              )}
            </DialogTitle>
          </DialogHeader>
          {selected?.status === "needs_attention" ? (
            <>
              <CoachPanel analysis={selected.analysis} onRetake={() => requestRetake(selected.id)} />
              {selected.analysis?.summary && selected.analysis?.readable !== false && (
                <div className="mt-4"><AnalysisView analysis={selected.analysis} /></div>
              )}
            </>
          ) : (
            <>
              {selected?.requires_approval && !selected?.approved && (
                <div className="rounded-xl border border-dox-orange/30 bg-dox-orange/[0.06] p-4 mb-2 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-white">{"I've prepared your package."}</div>
                    <div className="text-xs text-white/55">
                      {selected.analysis?.filing_fee && !["None", "Unknown"].includes(selected.analysis.filing_fee)
                        ? `Government fee: ${selected.analysis.filing_fee}. ` : ""}{"Say the word and I'll file it."}
                    </div>
                  </div>
                  <button data-testid="approve-btn" onClick={() => approve(selected.id)} className="shrink-0 rounded-full bg-dox-orange px-5 py-2.5 text-sm font-bold hover:bg-dox-orangeHover transition-colors">
                    Yes, Dox.
                  </button>
                </div>
              )}
              {selected?.status === "analyzed" && (
                <RenewalPackageButton docId={selected.id} />
              )}
              {selected?.approved && (
                <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3 mb-2 flex items-center gap-2 text-sm text-emerald-400">
                  <CheckCircle2 size={16} /> {"Done. I'm handling it from here."}
                </div>
              )}
              {selected && <AnalysisView analysis={selected.analysis} />}
            </>
          )}
        </DialogContent>
      </Dialog>

      <MorningBriefing open={briefingOpen} briefing={briefing} onDismiss={dismissBriefing} onPriority={briefingPriority} />
    </DashShell>
  );
}

/* ---------------- Overview ---------------- */
function Overview({ stats, docs, conflicts, priorities, onOpen, onGoDocs, onGoMemory, onNav }) {
  if (!stats) return <Skeleton />;
  const cards = [
    { label: "Documents", value: stats.total_documents, icon: FileStack, tone: "text-white" },
    { label: "Compliant", value: stats.compliance?.compliant || 0, icon: ShieldCheck, tone: "text-emerald-400" },
    { label: "Needs approval", value: stats.action_needed || 0, icon: AlertTriangle, tone: "text-amber-400" },
    { label: "Deadlines", value: stats.upcoming_deadlines?.length || 0, icon: CalendarClock, tone: "text-dox-magenta" },
  ];

  return (
    <div className="space-y-8">
      {/* Pixar-scale brand banner */}
      <BrandHero align="left" className="mb-2" />

      {/* Customer-delight status ribbon — quantified value moments */}
      <StatusRibbon />

      <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-surface to-elevated p-6 sm:p-8 relative overflow-hidden">
        <div className="glow-orb w-72 h-72 -right-16 -top-16 bg-dox-orange/15" />
        <div className="relative flex items-center gap-5">
          <div className="animate-float shrink-0"><DoxMascot size={64} /></div>
          <div>
            <h2 className="font-display font-black text-2xl sm:text-3xl">Morning. My team and I have you covered.</h2>
            <p className="text-white/55 mt-1 text-sm">
              My Document Specialist is watching {stats.total_documents} file{stats.total_documents === 1 ? "" : "s"}. My Calendar Specialist is on every deadline. You don&apos;t have to be.
            </p>
          </div>
        </div>
      </div>

      {/* The AI office at work — reinforces "you hired a whole team, not a chatbot" */}
      <TodaysActivity />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="glass-card p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <c.icon size={18} className={c.tone} />
            <div className="mt-3 font-display font-black text-3xl">{c.value}</div>
            <div className="text-xs text-white/45 uppercase tracking-wider mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {conflicts > 0 && (
        <button
          data-testid="verification-banner"
          onClick={onGoMemory}
          className="w-full text-left rounded-2xl border border-yellow-400/30 bg-yellow-400/[0.06] p-5 flex items-center justify-between gap-4 hover:bg-yellow-400/[0.1] transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-yellow-400/15 text-yellow-400 flex items-center justify-center"><AlertTriangle size={20} /></span>
            <div>
              <div className="font-semibold text-white">Dox needs you to verify {conflicts} item{conflicts === 1 ? "" : "s"}</div>
              <div className="text-sm text-white/55">New document info doesn't match your Business Memory. Nothing was overwritten.</div>
            </div>
          </div>
          <span className="text-yellow-400 text-sm font-bold shrink-0">Review →</span>
        </button>
      )}

      {priorities && (
        <div>
          <h3 className="font-display font-bold text-lg mb-4">Today's priorities</h3>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="daily-priorities">
            <PriorityCard title="Today's work" icon={ClipboardList} onMore={() => onNav("tasks")}
              items={priorities.today_work.map((t) => t.title)} empty="All caught up." />
            <PriorityCard title="Waiting approval" icon={AlertTriangle} tone="text-amber-400" onMore={() => onNav("documents")}
              items={priorities.waiting_approval.map((w) => `${w.title}${w.fee && !["None", "Unknown"].includes(w.fee) ? ` · ${w.fee}` : ""}`)} empty="Nothing awaiting you." />
            <PriorityCard title="Upcoming deadlines" icon={CalendarDays} tone="text-dox-magenta" onMore={() => onNav("calendar")}
              items={priorities.upcoming_deadlines.map((e) => `${e.title} · ${e.date}`)} empty="No deadlines scheduled." />
            <PriorityCard title="Suggested next steps" icon={ArrowRight} tone="text-dox-orange"
              items={priorities.suggested_next_steps} empty="You're in great shape." />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass-card p-6">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><CalendarClock size={18} className="text-dox-orange" /> Upcoming deadlines</h3>
          {stats.upcoming_deadlines?.length ? (
            <ul className="space-y-3">
              {stats.upcoming_deadlines.map((d, i) => (
                <li key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-3 last:border-0">
                  <div className="min-w-0">
                    <div className="text-white truncate">{d.label}</div>
                    <div className="text-white/40 text-xs truncate">{d.document}</div>
                  </div>
                  <span className="font-mono text-amber-400 shrink-0 ml-3">{d.date}</span>
                </li>
              ))}
            </ul>
          ) : <Empty text="No deadlines detected yet." />}
        </div>

        <div className="glass-card p-6">
          <h3 className="font-display font-bold text-lg mb-4">On Dox's desk</h3>
          {docs.length ? (
            <ul className="space-y-2">
              {docs.slice(0, 5).map((d) => (
                <li key={d.id}>
                  <button onClick={() => d.status !== "processing" && onOpen(d)} className="w-full flex items-center justify-between text-sm rounded-lg px-3 py-2.5 hover:bg-white/5 transition-colors" data-testid={`overview-doc-${d.id}`}>
                    <span className="flex items-center gap-2 min-w-0"><FileText size={15} className="text-white/40 shrink-0" /><span className="truncate">{d.original_filename}</span></span>
                    <span className="text-xs text-white/40 shrink-0 ml-2">{d.status === "processing" ? "working…" : d.category}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-6">
              <p className="text-white/50 text-sm mb-4">Nothing here yet.</p>
              <button onClick={onGoDocs} className="rounded-full bg-dox-orange px-5 py-2.5 text-sm font-bold hover:bg-dox-orangeHover transition-colors">Give Dox your first document</button>
            </div>
          )}
        </div>
      </div>

      {/* Dox's Work History — proof-of-value + expandable cards */}
      <WorkHistorySection variant="embedded" />
    </div>
  );
}

/* ---------------- Documents / Dox's Desk ---------------- */
function Documents({ docs, reload, onOpen, onRetake }) {
  const [giving, setGiving] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const give = async (file) => {
    if (!file) return;
    setGiving(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.message("Handed to Dox", { description: `Dox is on "${file.name}". Feel free to walk away.` });
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not hand this to Dox.");
    } finally {
      setGiving(false);
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.[0]) give(e.dataTransfer.files[0]); };
  const del = async (id, e) => { e.stopPropagation(); try { await api.delete(`/documents/${id}`); toast.success("Removed from Dox's desk."); await reload(); } catch { toast.error("Could not delete."); } };
  const reanalyze = async (id, e) => { e.stopPropagation(); try { await api.post(`/documents/${id}/reanalyze`); toast.message("Dox is taking another look…"); await reload(); } catch { toast.error("Could not re-run."); } };

  const working = docs.filter((d) => d.status === "processing");
  const attention = docs.filter((d) => d.status === "needs_attention");
  const done = docs.filter((d) => d.status !== "processing" && d.status !== "needs_attention");

  return (
    <div className="space-y-6">
      <div
        data-testid="give-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-dox-orange bg-dox-orange/5" : "border-white/15 hover:border-white/30 bg-surface"}`}
      >
        <input ref={inputRef} data-testid="give-input" type="file" className="hidden" onChange={(e) => give(e.target.files?.[0])} accept=".pdf,.png,.jpg,.jpeg,.txt,.csv,.docx" />
        <div className="flex flex-col items-center gap-3">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-dox-orange to-dox-magenta flex items-center justify-center text-white shadow-[0_0_28px_rgba(30,120,255,0.4)]">
            <Camera size={28} />
          </div>
          <div>
            <p className="font-display font-bold text-lg text-white">{giving ? "Handing it to Dox…" : "Give it to Dox"}</p>
            <p className="text-white/45 text-sm mt-1">Drop a document or snap a photo — Dox takes it from here. Then walk away.</p>
          </div>
        </div>
      </div>

      {working.map((d) => <DoxWorkingCard key={d.id} doc={d} />)}

      {attention.map((d) => (
        <div key={d.id} data-testid={`doc-attention-${d.id}`} className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2 text-sm text-white/60 min-w-0">
              <FileText size={15} className="text-amber-400 shrink-0" />
              <span className="truncate">{d.original_filename}</span>
            </div>
            <button onClick={() => onOpen(d)} className="text-xs text-white/50 hover:text-white shrink-0 ml-2">View details</button>
          </div>
          <CoachPanel analysis={d.analysis} onRetake={() => onRetake(d.id)} compact />
        </div>
      ))}

      {done.length === 0 && working.length === 0 && attention.length === 0 ? (
        <Empty text="Nothing on Dox's desk yet. Give Dox something to work on." />
      ) : done.length === 0 ? null : (
        <div className="grid sm:grid-cols-2 gap-4">
          {done.map((d) => {
            const st = d.analysis?.compliance_status || "unknown";
            const needsApproval = d.requires_approval && !d.approved;
            const tone = needsApproval ? "text-amber-400 bg-amber-500/15" : st === "compliant" ? "text-emerald-400 bg-emerald-500/15" : "text-white/60 bg-white/10";
            return (
              <div key={d.id} data-testid={`doc-card-${d.id}`} onClick={() => onOpen(d)} className="group cursor-pointer glass-card p-5 hover:border-dox-orange/40 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/50 shrink-0"><FileText size={18} /></div>
                    <div className="min-w-0">
                      <div className="font-medium text-white truncate">{d.original_filename}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-white/40">{d.category}</span>
                        <ConfidenceBadge analysis={d.analysis} />
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${tone}`}>
                    {needsApproval ? "Yes, Dox." : st === "compliant" ? "You're good." : "—"}
                  </span>
                </div>
                <p className="text-sm text-white/55 mt-3 line-clamp-2">{d.analysis?.summary}</p>
                <div className="mt-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => reanalyze(d.id, e)} data-testid={`doc-reanalyze-${d.id}`} className="flex items-center gap-1 text-xs text-white/50 hover:text-white px-2 py-1 rounded-lg hover:bg-white/5"><RefreshCw size={12} /> Ask Dox again</button>
                  <button onClick={(e) => del(d.id, e)} data-testid={`doc-delete-${d.id}`} className="flex items-center gap-1 text-xs text-red-400/70 hover:text-red-400 px-2 py-1 rounded-lg hover:bg-red-500/10"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DoxWorkingCard({ doc }) {
  return (
    <div data-testid={`doc-working-${doc.id}`} className="rounded-2xl border border-dox-orange/25 bg-dox-orange/[0.04] p-5 flex items-center gap-4 overflow-hidden">
      <div className="animate-float shrink-0"><DoxMascot size={44} /></div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-white truncate">{doc.original_filename}</div>
        <div className="flex items-center gap-2 mt-1 text-dox-orange">
          <Loader2 size={13} className="animate-spin" />
          <span className="text-sm truncate" data-testid={`doc-working-msg-${doc.id}`}>{doc.processing_message || "Dox is on it…"}</span>
        </div>
      </div>
      <span className="text-xs text-white/40 shrink-0 hidden sm:block">You can walk away</span>
    </div>
  );
}

/* ---------------- Talk to Dox ---------------- */
function AskDox({ hasDocs, doxStatus }) {
  const { user } = useAuth();
  const { active: activeBiz } = useBusiness();
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "there";
  const hourGreet = (() => {
    const h = new Date().getHours();
    if (h < 12) return "morning";
    if (h < 17) return "afternoon";
    return "evening";
  })();

  const ask = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    const question = q.trim();
    setMessages((m) => [...m, { role: "user", text: question }]);
    setQ("");
    setLoading(true);
    try {
      const { data } = await api.post("/ask", { question });
      setMessages((m) => [...m, { role: "dox", text: data.answer, verification: data.verification }]);
    } catch { toast.error("Dox couldn't answer that."); }
    finally { setLoading(false); }
  };

  const suggestions = [
    "What's due this week?",
    "Any renewals I should worry about?",
    "Summarise the latest document you reviewed",
    "What did you handle overnight?",
  ];

  return (
    <div className="max-w-3xl">
      {/* Employee-style header — feels like sitting down with a person, not opening a chatbot. */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 glass-strong p-6 sm:p-7 mb-5" data-testid="talk-to-dox-header">
        <div className="glow-orb w-[420px] h-[220px] -top-20 -right-20 bg-dox-purple/25" />
        <div className="relative flex items-start gap-5">
          <div className="animate-float shrink-0"><DoxMascot size={72} /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-black text-2xl sm:text-3xl leading-tight">
                Good {hourGreet}, <span className="text-gradient-brand">{firstName}</span>. I&apos;m Dox.
              </h2>
              <span className="text-xl">👋</span>
            </div>
            <p className="mt-2 text-white/70 leading-relaxed max-w-xl text-sm sm:text-base">
              I&apos;ve already reviewed {activeBiz?.name ? <span className="text-white font-semibold">{activeBiz.name}</span> : "your business"} this {hourGreet}.
              What would you like me to take care of next?
            </p>
            {doxStatus && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs text-white/75" data-testid="talk-status">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                {doxStatus.label}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 glass min-h-[420px] flex flex-col">
        <div className="flex-1 p-6 space-y-4 overflow-y-auto">
          {messages.length === 0 && (
            <div className="py-6">
              <p className="text-white/50 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Try asking</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid={`talk-suggestion-${s.slice(0, 10)}`}
                    onClick={() => setQ(s)}
                    className="text-sm text-white/80 rounded-full glass hover:bg-white/10 border border-white/10 px-3.5 py-2 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
              {!hasDocs && <p className="text-white/40 text-xs mt-4">Tip: give Dox some documents so I have more context to work from.</p>}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "dox" && <div className="shrink-0"><DoxMascot size={30} /></div>}
              <div className="max-w-[80%]">
                <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-dox-orange text-white" : "glass border border-white/10 text-white/85"}`}>
                  {m.text}
                </div>
                {m.role === "dox" && m.verification && <DoxVerifiedLine verification={m.verification} />}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <DoxMascot size={22} /> Dox is thinking…
            </div>
          )}
        </div>
        <form onSubmit={ask} className="p-4 border-t border-white/10 flex gap-3">
          <input data-testid="ask-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Ask Dox anything, ${firstName}…`} className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-3 text-sm text-white outline-none focus:border-dox-orange transition" />
          <button data-testid="ask-submit" disabled={loading} className="rounded-full bg-dox-orange w-12 h-12 flex items-center justify-center text-white hover:bg-dox-orangeHover disabled:opacity-60 transition-colors shrink-0">
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}

function Skeleton() {
  return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-white/5 animate-pulse" />)}</div>;
}

function PriorityCard({ title, icon: Icon, items, empty, tone = "text-white", onMore }) {
  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-bold text-sm flex items-center gap-2"><Icon size={15} className={tone} /> {title}</h4>
        {onMore && items.length > 0 && <button onClick={onMore} className="text-xs text-white/40 hover:text-white">View all</button>}
      </div>
      {items.length === 0 ? (
        <p className="text-white/35 text-sm">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 4).map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-white/70">
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${tone.replace("text-", "bg-")}`} />
              <span className="min-w-0">{t}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
function Empty({ text }) {
  return <div className="text-center py-12 text-white/40 text-sm">{text}</div>;
}

/* ---------------- Renewal Package Button ---------------- */
function RenewalPackageButton({ docId }) {
  const [loading, setLoading] = useState(false);
  const [pkgId, setPkgId] = useState(null);
  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/renewal-packages", { document_id: docId });
      setPkgId(data.id);
      const res = await api.get(`/renewal-packages/${data.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (data.title || "renewal-package").replace(/[^\w-]+/g, "-") + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dox prepared your renewal package.", { description: "PDF is downloading now." });
    } catch {
      toast.error("Dox couldn't assemble the package. Try again in a moment.");
    } finally { setLoading(false); }
  };
  return (
    <div className="rounded-xl border border-dox-purple/30 bg-dox-purple/[0.06] p-4 mb-2">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <FileSignature size={20} className="text-dox-purple shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">Filing package on demand</div>
            <div className="text-xs text-white/55">Dox assembles a cover letter, filing summary and fee sheet. Or hand it off to file for you.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button
            data-testid={`renewal-pkg-${docId}`}
            onClick={generate}
            disabled={loading}
            className="rounded-full glass-strong border border-dox-purple/40 px-4 py-2.5 text-sm font-bold text-white hover:bg-dox-purple/20 transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {loading ? "Dox is preparing…" : pkgId ? "Download again" : "Prepare package"}
          </button>
          {pkgId && <SubmitForMeButton packageId={pkgId} onSent={() => toast.success("Dox is handing this off to the filing partner.", { description: "Track status in the Filings tab." })} />}
        </div>
      </div>
    </div>
  );
}
